import React from 'react';

// Variant options
export type TypographyVariant = 
  | 'title-large'    // 48px, 600 weight
  | 'title-medium'          // 32px, 600 weight  
  | 'title'   // 24px, 600 weight
  | 'body-large'      // 24px, 600 weight
  | 'body-medium'    // 16px, 500 weight
  | 'body'           // 14px, 400 weight
  | 'caption'        // 12px, 500 weight
  | 'nav'            // 14px, 500 weight (nav specific)
  | 'custom';

// Color variants 
export type TypographyColor = 
  | 'primary'        // #1A1B1D (main text - foreground)
  | 'secondary'      // rgba(26, 27, 29, 0.50) (foreground-secondary)
  | 'tertiary'       // rgba(26, 27, 29, 0.70) (foreground-tertiary)
  | 'muted'          // #1C1F1D (foreground-muted)
  | 'muted-secondary' // rgba(28, 31, 29, 0.60) (foreground-muted-secondary)
  | 'brand'          // #00A859 (primary brand color)
  | 'white'
  | 'inherit'
  | 'custom';

// Alignment options
export type TypographyAlign = 'left' | 'center' | 'right' | 'justify';

// HTML element mapping for semantic correctness
export type TypographyElement = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span' | 'div' | 'label';

interface TypographyProps {
  variant?: TypographyVariant;
  color?: TypographyColor;
  align?: TypographyAlign;
  as?: TypographyElement;
  className?: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
  htmlFor?: string;
}

const Typography: React.FC<TypographyProps> = ({
  variant = 'body',
  color = 'primary',
  align = 'left',
  as = 'p',
  className = '',
  children,
  style,
  ...props
}) => {
  // Variant styles mapping from your Figma specs
  const variantClasses = {
    'title-large': 'text-3xl md:text-4xl xl:text-5xl font-semibold font-sans leading-tight', // 48px, 600 weight
    'title-medium': 'text-2xl lg:text-3xl font-semibold leading-tight font-sans', // 32px, 600 weight
    'title': 'text-lg lg:text-[28px] font-semibold leading-tight font-sans',       // 24px, 600 weight
    'body-large': 'text-base lg:text-[20px] font-semibold leading-tight font-sans', // 18px, 600 weight
    'body-medium': 'text-sm sm:text-base font-normal leading-normal font-sans',        // 16px, 400 weight
    'body': 'text-sm font-medium leading-normal font-sans',   // 14px, 500 weight
    'caption': 'text-xs font-medium leading-normal font-sans',       // 12px, 500 weight
    'nav': 'text-sm font-medium leading-normal font-sans',           // 14px, 500 weight
    'custom': ''
  };

  // Color classes based on your design tokens
  const colorClasses = {
    'primary': 'text-foreground',                    // #1A1B1D
    'secondary': 'text-foreground-secondary',        // rgba(26, 27, 29, 0.50)
    'tertiary': 'text-foreground-tertiary',          // rgba(26, 27, 29, 0.70)
    'muted': 'text-foreground-muted',                // #1C1F1D
    'muted-secondary': 'text-foreground-muted-secondary', // rgba(28, 31, 29, 0.60)
    'brand': 'text-primary-500',                     // #00A859
    'white': 'text-white',
    'inherit': 'text-inherit',
    'custom': ''
  };

  // Alignment classes
  const alignClasses = {
    'left': 'text-left',
    'center': 'text-center',
    'right': 'text-right',
    'justify': 'text-justify'
  };

  // Combine all classes
  const combinedClasses = [
    variantClasses[variant],
    colorClasses[color],
    alignClasses[align],
    className
  ].filter(Boolean).join(' ');

  // Create the component dynamically
  const Component = as;

  return (
    <Component 
      className={combinedClasses}
      style={style}
      {...props}
    >
      {children}
    </Component>
  );
};

// Convenience components for common use cases
export const Title: React.FC<Omit<TypographyProps, 'variant' | 'as'> & { level?: 1 | 2  | 3}> = ({ 
  level = 1, 
  ...props 
}) => (
  <Typography 
    variant={level === 1 ? 'title-large' : level === 2 ? 'title-medium' :  'title'} 
    as={level === 1 ? 'h1' : level === 2 ? 'h2'  : 'h3'} 
    {...props} 
  />
);

export const Subtitle: React.FC<Omit<TypographyProps,   'as'>> = (props) => (
  <Typography variant="body" color="secondary" as="p" {...props} />
);

export const Body: React.FC<TypographyProps> = (props) => (
  <Typography variant="body" {...props} />
);

export const Caption: React.FC<Omit<TypographyProps, 'variant'>> = (props) => (
  <Typography variant="caption" {...props} />
);

export const NavText: React.FC<Omit<TypographyProps, 'variant'>> = (props) => (
  <Typography variant="nav" color="tertiary" {...props} />
);
// Brand-specific text component
export const BrandText: React.FC<Omit<TypographyProps, 'color'>> = (props) => (
  <Typography color="brand" {...props} />
);
// Export the main component and convenience components
export default Typography;